import { WorkbookType } from '@/components/content-generator/workbook/new-workbook-smart-detection';

export type PageType = 
  | 'Blog Post' 
  | 'Article' 
  | 'Social Media Post' 
  | 'Marketing Copy' 
  | 'Strategic Plan' 
  | 'Meeting Agenda' 
  | 'Research Brief' 
  | 'Email Template'
  | 'Newsletter'
  | 'Case Study'
  | 'Whitepaper'
  | 'Press Release'
  | 'Proposal'
  | 'Report'
  | 'Presentation'
  | 'Product Description'
  | 'Landing Page'
  | 'FAQ'
  | 'Tutorial'
  | 'Policy Document'
  | 'Other';

export const pageTypeOptions: { value: PageType; label: string }[] = [
  { value: 'Blog Post', label: 'Blog Post' },
  { value: 'Article', label: 'Article' },
  { value: 'Social Media Post', label: 'Social Media Post' },
  { value: 'Marketing Copy', label: 'Marketing Copy' },
  { value: 'Strategic Plan', label: 'Strategic Plan' },
  { value: 'Meeting Agenda', label: 'Meeting Agenda' },
  { value: 'Research Brief', label: 'Research Brief' },
  { value: 'Email Template', label: 'Email Template' },
  { value: 'Newsletter', label: 'Newsletter' },
  { value: 'Case Study', label: 'Case Study' },
  { value: 'Whitepaper', label: 'Whitepaper' },
  { value: 'Press Release', label: 'Press Release' },
  { value: 'Proposal', label: 'Proposal' },
  { value: 'Report', label: 'Report' },
  { value: 'Presentation', label: 'Presentation' },
  { value: 'Product Description', label: 'Product Description' },
  { value: 'Landing Page', label: 'Landing Page' },
  { value: 'FAQ', label: 'FAQ' },
  { value: 'Tutorial', label: 'Tutorial' },
  { value: 'Policy Document', label: 'Policy Document' },
  { value: 'Other', label: 'Other' },
];

// Simplified function - now returns all page types regardless of workbook type
export const getPageOptionsForWorkbook = (workbookType?: WorkbookType): { value: PageType; label: string }[] => {
  // Return all page types - no more filtering based on workbook type
  return pageTypeOptions;
};

// Simple keyword-based detection with single words - optimized for natural language
const detectionKeywords: { [key in PageType]?: string[] } = {
  'Blog Post': [
    'blog', 'blogging', 'blogger', 'personal', 'lifestyle', 'opinion', 
    'thoughts', 'experience', 'journey', 'story', 'diary', 'weekly', 'daily'
  ],
  'Article': [
    'article', 'journalism', 'editorial', 'investigative', 'publication',
    'magazine', 'newspaper', 'coverage', 'interview', 'feature'
  ],
  'Social Media Post': [
    'social media', 'instagram', 'facebook', 'twitter', 'linkedin', 'tiktok', 'snapchat',
    'viral', 'hashtag', 'influencer', 'followers', 'likes', 'comments', 'reel'
  ],
  'Marketing Copy': [
    'marketing', 'advertising', 'promotional', 'branding', 'conversion',
    'funnel', 'cta', 'sales', 'campaign', 'launch', 'offer', 'discount'
  ],
  'Strategic Plan': [
    'strategic', 'strategy', 'roadmap', 'planning', 'objectives', 'vision',
    'mission', 'goals', 'kpi', 'metrics', 'forecast', 'organizational'
  ],
  'Meeting Agenda': [
    'meeting', 'agenda', 'minutes', 'conference', 'workshop', 'session',
    'briefing', 'standup', 'sync', 'kickoff', 'retrospective'
  ],
  'Research Brief': [
    'research', 'findings', 'insights', 'methodology', 'results', 'conclusion',
    'recommendation', 'evidence', 'statistics', 'trends', 'survey', 'investigation'
  ],
  'Email Template': [
    'email', 'template', 'message', 'correspondence', 'outreach', 
    'welcome', 'invitation', 'automation', 'sequence'
  ],
  'Newsletter': [
    'newsletter', 'bulletin', 'digest', 'update', 'edition', 'issue', 'subscriber'
  ],
  'Case Study': [
    'case', 'success', 'customer', 'client', 'implementation', 'testimonial',
    'transformation', 'outcome', 'results'
  ],
  'Whitepaper': [
    'whitepaper', 'comprehensive', 'authoritative', 'technical', 'detailed',
    'expert', 'guide', 'analysis'
  ],
  'Press Release': [
    'press', 'announcement', 'launch', 'statement', 'public'
  ],
  'Proposal': [
    'proposal', 'quote', 'estimate', 'bid', 'offer', 'pitch', 'partnership',
    'collaboration', 'solution'
  ],
  'Report': [
    'report', 'quarterly', 'annual', 'financial', 'performance', 'status',
    'progress', 'evaluation', 'assessment'
  ],
  'Presentation': [
    'presentation', 'slides', 'deck', 'powerpoint', 'keynote', 'demo',
    'webinar', 'training'
  ],
  'Product Description': [
    'product', 'features', 'benefits', 'specifications', 'catalog', 'listing',
    'description', 'details', 'overview'
  ],
  'Landing Page': [
    'landing', 'conversion', 'capture', 'signup', 'squeeze', 'optin'
  ],
  'FAQ': [
    'faq', 'questions', 'answers', 'help', 'support', 'troubleshooting'
  ],
  'Tutorial': [
    'tutorial', 'walkthrough', 'instructional', 'lesson', 'course',
    'educational', 'teaching', 'learning'
  ],
  'Policy Document': [
    'policy', 'procedure', 'guidelines', 'terms', 'privacy', 'compliance',
    'handbook', 'manual', 'standard'
  ],
};

export const detectPageType = (text: string, availableTypes: PageType[]): PageType | undefined => {
  const lowerText = text.toLowerCase();
  
  // Enhanced scoring system for single word detection
  const scores: { [key in PageType]?: number } = {};
  
  for (const pageType of availableTypes) {
    const keywords = detectionKeywords[pageType];
    if (keywords) {
      let score = 0;
      for (const keyword of keywords) {
        // Use word boundary regex for precise matching
        const wordBoundaryRegex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (wordBoundaryRegex.test(text)) {
          // Score based on keyword specificity and length
          let keywordScore = 1;
          
          // Give higher scores for compound keywords (contain spaces)
          if (keyword.includes(' ')) {
            keywordScore = 4; // Compound keywords are very specific
          } else if (keyword.length > 10) {
            keywordScore = 3; // Very specific words
          } else if (keyword.length > 7) {
            keywordScore = 2; // Specific words
          } else {
            keywordScore = 1; // Common words
          }
          
          score += keywordScore;
        }
      }
      if (score > 0) {
        scores[pageType] = score;
      }
    }
  }
  
  // Return the page type with the highest score, with confidence threshold
  if (Object.keys(scores).length > 0) {
    const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const topScore = sortedScores[0][1];
    const secondScore = sortedScores[1]?.[1] || 0;
    
    // Only return a detection if there's a clear winner
    if (topScore > secondScore) {
      return sortedScores[0][0] as PageType;
    }
  }
  
  return undefined;
};
